import React, { useCallback, useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import { findAllParent, findMenuItem, MenuItemType } from "../menu"
import MenuItem from "./MenuItem"
import MenuItemWithChildren from "./MenuItemWithChildren"
import { useAuthenticatedUser } from "auth-provider"
import useUserStore from "stores/user"

type AppMenuProps = {
  menuItems: Array<MenuItemType>
}

const AppMenu = ({ menuItems }: AppMenuProps) => {
  const location = useLocation()
  const { user } = useAuthenticatedUser()
  const isCommonCarrier = useUserStore((state) => state.isCommonCarrier)
  const isCC = useUserStore((state) => state.isCC)
  const isCustomer = useUserStore((state) => state.isCustomer)
  const isExternal = useUserStore((state) => state.isExternal)
  const isDriver = useUserStore((state) => state.isDriver)

  const menuRef = useRef<HTMLUListElement>(null)

  const [activeMenuItems, setActiveMenuItems] = useState<Array<string>>([])

  /*
   * toggle the menus
   */
  const toggleMenu = (menuItem: MenuItemType, show: boolean) => {
    if (show)
      setActiveMenuItems([
        menuItem["key"],
        ...findAllParent(menuItems, menuItem),
      ])
  }

  /**
   * activate the menuitems
   */
  const activeMenu = useCallback(() => {
    const div = document.getElementById("main-side-menu")
    let matchingMenuItem = null

    if (div) {
      const items: HTMLCollectionOf<HTMLAnchorElement> =
        div.getElementsByClassName(
          "side-nav-link-ref"
        ) as HTMLCollectionOf<HTMLAnchorElement>
      for (let i = 0; i < items.length; ++i) {
        if (location.pathname === items[i].pathname) {
          matchingMenuItem = items[i]
          break
        }
      }

      if (matchingMenuItem) {
        const mid = matchingMenuItem.getAttribute("data-menu-key")
        const activeMt = findMenuItem(menuItems, mid!)
        if (activeMt) {
          setActiveMenuItems([
            activeMt["key"],
            ...findAllParent(menuItems, activeMt),
          ])
        }
      }
    }
  }, [location.pathname, menuItems])

  useEffect(() => {
    activeMenu()
  }, [activeMenu])

  return (
    <ul className="side-nav" ref={menuRef} id="main-side-menu">
      {(menuItems || []).map((item, index) => {
        if (item?.hide) return null
        if (isDriver && !item.showDriver) return null
        if (isCustomer && !item.showForCustomer) return null
        if (isExternal && item.hideForExternal) return null
        if (isCommonCarrier && item.hideForCC) return null
        if (isCC && item.hideForAnyCC) return null
        if (item.adminOnly && !user?.groups?.includes("Admin")) return null

        return (
          <React.Fragment key={index.toString()}>
            {item.isTitle ? (
              <li className="side-nav-title side-nav-item">{item.label}</li>
            ) : (
              <>
                {item.children ? (
                  <MenuItemWithChildren
                    item={item}
                    toggleMenu={toggleMenu}
                    subMenuClassNames="side-nav-second-level"
                    activeMenuItems={activeMenuItems}
                    linkClassName="side-nav-link"
                  />
                ) : (
                  <MenuItem
                    item={item}
                    linkClassName="side-nav-link"
                    className={
                      activeMenuItems.includes(item.key)
                        ? "menuitem-active"
                        : ""
                    }
                  />
                )}
              </>
            )}
          </React.Fragment>
        )
      })}
    </ul>
  )
}

export default AppMenu
